import type { FC, ReactNode } from 'react'

import { useEffect, useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { ArrowRight, Github, Menu, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { githubUrl } from '@/lib/links'
import { Logo } from '@/components/generals/logo'
import { ButtonLink } from '@/components/generals/button-link'
import Container from '@/components/generals/container'
import SmartLink from '@/components/generals/smart-link'

import { navigation } from '@/constants/site'

// Sections of the home page and the navigation item each one belongs to.
const sectionToNav: Record<string, string> = {
    'overview': '/#overview',
    'how-it-works': '/#how-it-works',
    'features': '/#features',
    'screenshots': '/#screenshots',
    'explainability': '/#features',
    'workspace': '/#features',
    'desktop': '/#features',
    'download': '/#download',
    'install': '/#download'
}

// The header fades in on the first page load only, not on every navigation.
let introPlayed = false

const SiteHeader: FC = (): ReactNode => {
    const router = useRouter()
    const [scrolled, setScrolled] = useState(false)
    const [menuOpen, setMenuOpen] = useState(false)
    const [activeHref, setActiveHref] = useState<string | null>(null)
    const [intro] = useState(() => !introPlayed)

    useEffect(() => {
        introPlayed = true
    }, [])

    useEffect(() => {
        let frame = 0

        const read = () => {
            frame = 0
            setScrolled(window.scrollY > 8)
        }

        const onScroll = () => {
            if (!frame) frame = window.requestAnimationFrame(read)
        }

        read()
        window.addEventListener('scroll', onScroll, { passive: true })

        return () => {
            window.removeEventListener('scroll', onScroll)
            if (frame) window.cancelAnimationFrame(frame)
        }
    }, [])

    useEffect(() => {
        if (router.pathname !== '/' || !('IntersectionObserver' in window)) {
            setActiveHref(null)
            return
        }

        const sections = Object.keys(sectionToNav)
            .map(id => document.getElementById(id))
            .filter((element): element is HTMLElement => element !== null)

        const visible = new Set<string>()

        const observer = new IntersectionObserver((entries) => {
            for (const entry of entries) {
                if (entry.isIntersecting) visible.add(entry.target.id)
                else visible.delete(entry.target.id)
            }

            const current = sections.find(section => visible.has(section.id))

            setActiveHref(current ? sectionToNav[current.id] : null)
        }, { rootMargin: '-45% 0px -50% 0px' })

        sections.forEach(section => observer.observe(section))

        return () => observer.disconnect()
    }, [router.pathname])

    useEffect(() => {
        const close = () => setMenuOpen(false)

        router.events.on('routeChangeStart', close)
        router.events.on('hashChangeStart', close)

        return () => {
            router.events.off('routeChangeStart', close)
            router.events.off('hashChangeStart', close)
        }
    }, [router.events])

    useEffect(() => {
        if (!menuOpen) return

        const previousOverflow = document.body.style.overflow

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setMenuOpen(false)
        }

        const onResize = () => {
            if (window.innerWidth >= 1024) setMenuOpen(false)
        }

        document.body.style.overflow = 'hidden'
        window.addEventListener('keydown', onKeyDown)
        window.addEventListener('resize', onResize)

        return () => {
            document.body.style.overflow = previousOverflow
            window.removeEventListener('keydown', onKeyDown)
            window.removeEventListener('resize', onResize)
        }
    }, [menuOpen])

    const closeMenu = () => setMenuOpen(false)

    return (
        <header
            className={cn(
                'sticky top-0 z-50 border-b transition-[background-color,border-color,box-shadow] duration-300 ease-out-soft',
                scrolled || menuOpen ? 'border-taupe/80 bg-ivory/[0.97] shadow-[0_8px_24px_-20px_rgba(53,28,26,0.35)]' : 'border-transparent bg-ivory',
                intro && 'animate-in fade-in duration-500 ease-out-soft fill-mode-backwards motion-reduce:animate-none'
            )}
        >
            <Container className='flex h-[72px] items-center gap-6'>
                <Link href='/' aria-label="Ocuvia, home" className='rounded-md' onClick={closeMenu}>
                    <Logo />
                </Link>

                <nav aria-label='Primary' className='mx-auto hidden lg:block'>
                    <ul className='flex items-center gap-9'>
                        {navigation.map(item => (
                            <li key={item.href}>
                                <Link
                                    href={item.href}
                                    aria-current={activeHref === item.href ? 'location' : undefined}
                                    className={cn(
                                        'relative py-2 text-[14px] transition-colors duration-[250ms] ease-out-soft hover:text-ink after:absolute after:inset-x-0 after:bottom-0.5 after:h-px after:origin-left after:bg-terracotta after:transition-transform after:duration-500 after:ease-out-soft hover:after:scale-x-100',
                                        activeHref === item.href ? 'text-ink after:scale-x-100' : 'text-ink-soft after:scale-x-0'
                                    )}
                                >
                                    {item.label}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </nav>

                <div className='ml-auto flex items-center gap-2 lg:ml-0'>
                    <SmartLink
                        href={githubUrl}
                        className='hidden h-10 items-center gap-2 rounded-full px-4 text-[13.5px] font-medium text-ink-soft transition-colors duration-300 hover:text-ink md:inline-flex'
                        pendingClassName='hidden h-10 items-center gap-2 px-3 text-[13.5px] font-medium text-ink-soft/80 md:inline-flex'
                    >
                        <Github aria-hidden='true' className='size-4' />
                        GitHub
                    </SmartLink>

                    <ButtonLink href='/#download' size='sm' className='hidden sm:inline-flex'>
                        Download App
                    </ButtonLink>

                    <button
                        type='button'
                        aria-expanded={menuOpen}
                        aria-controls='mobile-menu'
                        aria-label={menuOpen ? "Close menu" : "Open menu"}
                        onClick={() => setMenuOpen(open => !open)}
                        className='grid size-10 place-items-center rounded-full border border-taupe text-ink transition-colors duration-300 hover:border-ink/40 lg:hidden'
                    >
                        {menuOpen ? <X aria-hidden='true' className='size-5' /> : <Menu aria-hidden='true' className='size-5' />}
                    </button>
                </div>
            </Container>

            <div
                id='mobile-menu'
                inert={!menuOpen}
                className={cn(
                    'absolute inset-x-0 top-full h-[calc(100dvh-72px)] overflow-y-auto overscroll-contain border-t border-taupe bg-ivory transition-[opacity,translate,visibility] duration-300 ease-out-soft lg:hidden',
                    menuOpen ? 'visible translate-y-0 opacity-100' : 'invisible -translate-y-2 opacity-0'
                )}
            >
                <Container className='flex min-h-full flex-col py-6'>
                    <nav aria-label='Mobile'>
                        <ul className='border-b border-taupe'>
                            {navigation.map(item => (
                                <li key={item.href} className='border-t border-taupe'>
                                    <Link
                                        href={item.href}
                                        onClick={closeMenu}
                                        className='flex items-center justify-between py-5 font-heading text-[2rem] leading-none text-ink'
                                    >
                                        {item.label}
                                        <ArrowRight aria-hidden='true' className='size-5 text-ink-soft' />
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </nav>

                    <div className='mt-auto flex flex-col gap-3 pt-10 pb-4'>
                        <ButtonLink href='/#download' className='w-full'>
                            Download Ocuvia
                        </ButtonLink>

                        <SmartLink
                            href={githubUrl}
                            onNavigate={closeMenu}
                            className='inline-flex h-12 w-full items-center justify-center gap-2 rounded-full border border-ink/15 text-[15px] font-medium text-ink'
                            pendingClassName='inline-flex h-12 w-full items-center justify-center gap-2 rounded-full border border-dashed border-ink/20 text-[15px] font-medium text-ink-soft'
                        >
                            <Github aria-hidden='true' className='size-4' />
                            GitHub
                        </SmartLink>
                    </div>
                </Container>
            </div>
        </header>
    )
}

export default SiteHeader
